import { DevelopmentSite, IDevelopmentSite } from '@app/models/DevelopmentSite'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface IDevelopmentProjectsListState {
  readonly city: string
  readonly client: string
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: List<IDevelopmentSite>
  readonly orderBy: string
  readonly sortOrder: string
  readonly status: string
  readonly totalItems: number
}

const DevelopmentProjectsListStateRecord = Record({
  city: '',
  client: '',
  error: undefined,
  filterText: '',
  isLoading: false,
  items: List<DevelopmentSite>(),
  orderBy: 'Ascending',
  sortOrder: 'Status',
  status: '',
  totalItems: 0,
})

export class DevelopmentProjectsListState
  extends DevelopmentProjectsListStateRecord
  implements IDevelopmentProjectsListState
{
  public static fromJS(initialState?: Partial<IDevelopmentProjectsListState>) {
    if (!initialState) {
      return new DevelopmentProjectsListState()
    }

    const { items, ...rest } = initialState
    return new DevelopmentProjectsListState({
      items: items
        ? List(items.map((i) => DevelopmentSite.fromJS(i)))
        : undefined,
      ...rest,
    })
  }

  public readonly city: string
  public readonly client: string
  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: List<DevelopmentSite>
  public readonly orderBy: string
  public readonly sortOrder: string
  public readonly status: string
  public readonly totalItems: number

  constructor(props?: Partial<DevelopmentProjectsListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum DevelopmentProjectsListActionTypes {
  LoadDevelopmentProjects = 'DEVELOPMENTPROJECTS_LIST::LOAD_DEVELOPMENTPROJECTS',
  LoadDevelopmentProjectsFailure = 'DEVELOPMENTPROJECTS_LIST::LOAD_DEVELOPMENTPROJECTS_FAILURE',
  LoadDevelopmentProjectsRequest = 'DEVELOPMENTPROJECTS_LIST::LOAD_DEVELOPMENTPROJECTS_REQUEST',
  LoadDevelopmentProjectsSuccess = 'DEVELOPMENTPROJECTS_LIST::LOAD_DEVELOPMENTPROJECTS_SUCCESS',
  SetAssignedTo = 'DEVELOPMENTPROJECTS_LIST::SET_ASSIGNED_TO',
  SetCity = 'DEVELOPMENTPROJECTS_LIST::SET_CITY',
  SetClient = 'DEVELOPMENTPROJECTS_LIST::SET_CLIENT',
  SetDueDate = 'DEVELOPMENTPROJECTS_LIST::SET_DUE_DATE',
  SetFilterText = 'DEVELOPMENTPROJECTS_LIST::SET_FILTER_TEXT',
  SetOrderBy = 'DEVELOPMENTPROJECT_LIST::SET_ORDER_BY',
  SetScope = 'DEVELOPMENTPROJECTS_LIST::SET_SCOPE',
  SetStatus = 'DEVELOPMENTPROJECTS_LIST::SET_STATUS',
  SetSortOrder = 'DEVELOPMENTPROJECTS_LIST::SET_SORT_ORDER',
}

interface ILoadDevelopmentProjectsAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: DevelopmentProjectsListActionTypes.LoadDevelopmentProjects
}

interface ILoadDevelopmentProjectsFailureAction {
  error: string
  type: DevelopmentProjectsListActionTypes.LoadDevelopmentProjectsFailure
}

interface ILoadDevelopmentProjectsRequestAction {
  type: DevelopmentProjectsListActionTypes.LoadDevelopmentProjectsRequest
}

interface ILoadDevelopmentProjectsSuccessAction {
  append: boolean
  items: DevelopmentSite[]
  totalItems: number
  type: DevelopmentProjectsListActionTypes.LoadDevelopmentProjectsSuccess
}

interface ISetCityAction {
  city: string
  type: DevelopmentProjectsListActionTypes.SetCity
}

interface ISetClientAction {
  client: string
  type: DevelopmentProjectsListActionTypes.SetClient
}

interface ISetFilterTextAction {
  filter: string
  type: DevelopmentProjectsListActionTypes.SetFilterText
}

interface ISetOrderByAction {
  orderBy: string
  type: DevelopmentProjectsListActionTypes.SetOrderBy
}

interface ISetSortOrderAction {
  sortOrder: string
  type: DevelopmentProjectsListActionTypes.SetSortOrder
}

interface ISetStatusAction {
  status: string
  type: DevelopmentProjectsListActionTypes.SetStatus
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadDevelopmentProjectsFailureAction
  | ILoadDevelopmentProjectsRequestAction
  | ILoadDevelopmentProjectsSuccessAction
  | ISetCityAction
  | ISetClientAction
  | ISetFilterTextAction
  | ISetOrderByAction
  | ISetSortOrderAction
  | ISetStatusAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadDevelopmentProjects(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadDevelopmentProjectsAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: DevelopmentProjectsListActionTypes.LoadDevelopmentProjects,
  }
}

function loadDevelopmentProjectsFailure(
  error: string,
): ILoadDevelopmentProjectsFailureAction {
  return {
    error,
    type: DevelopmentProjectsListActionTypes.LoadDevelopmentProjectsFailure,
  }
}

function loadDevelopmentProjectsRequest(): ILoadDevelopmentProjectsRequestAction {
  return {
    type: DevelopmentProjectsListActionTypes.LoadDevelopmentProjectsRequest,
  }
}

function loadDevelopmentProjectsSuccess(
  items: DevelopmentSite[],
  append: boolean,
  totalItems: number,
): ILoadDevelopmentProjectsSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: DevelopmentProjectsListActionTypes.LoadDevelopmentProjectsSuccess,
  }
}

function setCity(city: string): ISetCityAction {
  return {
    city,
    type: DevelopmentProjectsListActionTypes.SetCity,
  }
}

function setClient(client: string): ISetClientAction {
  return {
    client,
    type: DevelopmentProjectsListActionTypes.SetClient,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: DevelopmentProjectsListActionTypes.SetFilterText,
  }
}

function setOrderBy(orderBy: string) {
  return {
    orderBy,
    type: DevelopmentProjectsListActionTypes.SetOrderBy,
  }
}

function setSortOrder(sortOrder: string) {
  return {
    sortOrder,
    type: DevelopmentProjectsListActionTypes.SetSortOrder,
  }
}

function setStatus(status: string): ISetStatusAction {
  return {
    status,
    type: DevelopmentProjectsListActionTypes.SetStatus,
  }
}

export const actionCreators = {
  loadDevelopmentProjects,
  setCity,
  setClient,
  setFilterText,
  setOrderBy,
  setSortOrder,
  setStatus,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<DevelopmentProjectsListState> = (
  state: DevelopmentProjectsListState = new DevelopmentProjectsListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case DevelopmentProjectsListActionTypes.LoadDevelopmentProjectsFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as DevelopmentProjectsListState

    case DevelopmentProjectsListActionTypes.LoadDevelopmentProjectsRequest:
      return state.set('isLoading', true) as DevelopmentProjectsListState

    case DevelopmentProjectsListActionTypes.LoadDevelopmentProjectsSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<DevelopmentSite>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as DevelopmentProjectsListState

    case DevelopmentProjectsListActionTypes.SetCity:
      return state.set('city', action.city) as DevelopmentProjectsListState

    case DevelopmentProjectsListActionTypes.SetClient:
      return state.set('client', action.client) as DevelopmentProjectsListState

    case DevelopmentProjectsListActionTypes.SetFilterText:
      return state.set(
        'filterText',
        action.filter,
      ) as DevelopmentProjectsListState

    case DevelopmentProjectsListActionTypes.SetOrderBy:
      return state.set(
        'orderBy',
        action.orderBy,
      ) as DevelopmentProjectsListState

    case DevelopmentProjectsListActionTypes.SetSortOrder:
      return state.set(
        'sortOrder',
        action.sortOrder,
      ) as DevelopmentProjectsListState

    case DevelopmentProjectsListActionTypes.SetStatus:
      return state.set('status', action.status) as DevelopmentProjectsListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadDevelopmentProjectsSaga(action: ILoadDevelopmentProjectsAction) {
  try {
    yield put(loadDevelopmentProjectsRequest())

    const currentState: DevelopmentProjectsListState = yield select(
      (state: ApplicationState) => state.developmentProjectsList,
    )

    const query: string[] = []

    if (currentState.city && currentState.city !== 'All') {
      query.push(`city=${currentState.city}`)
    }

    if (currentState.client && currentState.client !== 'All') {
      query.push(`client=${currentState.client}`)
    }

    if (currentState.sortOrder) {
      query.push(`sort=${currentState.sortOrder}`)
    }

    if (currentState.orderBy) {
      query.push(`orderBy=${currentState.orderBy}`)
    }

    if (currentState.status) {
      query.push(`status=${currentState.status}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    const baseUrl = '/api/developmentSites/my'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: DevelopmentSite[] = result.results.map((c: any) =>
        DevelopmentSite.fromJS(c),
      )
      yield put(
        loadDevelopmentProjectsSuccess(
          contacts,
          action.append,
          result.paging.total,
        ),
      )
    } else {
      yield put(loadDevelopmentProjectsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadDevelopmentProjectsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadDevelopmentProjects() {
  yield takeLatest(
    DevelopmentProjectsListActionTypes.LoadDevelopmentProjects,
    loadDevelopmentProjectsSaga,
  )
}

export function* developmentProjectsListRootSaga() {
  yield fork(watchLoadDevelopmentProjects)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState =
  () =>
  (state: ApplicationState): DevelopmentProjectsListState =>
    state.developmentProjectsList

export const selectCity = () =>
  createSelector(selectModuleState(), (mState) => mState.city)

export const selectClient = () =>
  createSelector(selectModuleState(), (mState) => mState.client)
export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectFilterText = () =>
  createSelector(selectModuleState(), (mState) => mState.filterText)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectItems = () =>
  createSelector(selectModuleState(), (mState) => mState.items)

export const selectOrderBy = () =>
  createSelector(selectModuleState(), (mstate) => mstate.orderBy)

export const selectSortOrder = () =>
  createSelector(selectModuleState(), (mState) => mState.sortOrder)

export const selectStatus = () =>
  createSelector(selectModuleState(), (mState) => mState.status)

export const selectTotalItems = () =>
  createSelector(selectModuleState(), (mState) => mState.totalItems)
